import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Calculator } from "lucide-react";
import { money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { splitIncGst } from "@/lib/priceCalc";

const Row = ({ label, value, strong, tone, testid }) => (
  <div
    className={`grid grid-cols-[1fr_auto] items-baseline gap-3 px-3 py-2.5 ${
      tone === "gold" ? "bg-[#C5A059] text-[#0A0A0A]" : ""
    }`}
  >
    <span className={`text-sm ${tone === "gold" ? "font-semibold" : "text-muted-foreground"}`}>
      {label}
    </span>
    <span
      className={`tabular text-right ${strong ? "text-base font-bold" : "text-sm font-semibold"}`}
      data-testid={testid}
    >
      {value}
    </span>
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </Label>
    {children}
  </div>
);

const numInput = { type: "number", inputMode: "decimal", step: "0.01", min: "0" };

/** Standalone Ex-GST calculator. One question: what will the customer pay in total (inc GST)?
 *  It works backwards to the correct ex-GST price so GST stays a true 10% of the real sale.
 *  Nothing here changes an invoice — to apply a deal, use "Negotiate price" on the invoice. */
export default function ExGstCalculator() {
  const [params] = useSearchParams();
  const invoiceNo = params.get("invoice") || "";
  const ctxTotal = params.get("total") || "";
  const ctxGst = params.get("gst") || "10";

  const [agreed, setAgreed] = useState("");
  const [gst, setGst] = useState(ctxGst);

  const gstRate = Number(gst) || 0;
  const agreedNum = Number(agreed) || 0;
  const hasAgreed = agreed !== "" && agreedNum > 0;
  const split = splitIncGst(agreedNum, gstRate);
  const inputCls = "rounded-none";

  return (
    <div className="fade-up max-w-2xl" data-testid="exgst-calculator-page">
      <div className="flex items-center gap-3">
        <Calculator className="h-7 w-7 shrink-0 text-[#C5A059]" />
        <h1 className="text-3xl font-bold sm:text-4xl">Ex-GST Calculator</h1>
      </div>
      <p className="mt-2 text-sm text-muted-foreground md:text-base">
        Type the total the customer will pay (GST inclusive) and you'll instantly see the correct
        ex-GST price and the GST inside it. GST stays a true 10% of the real sale price.
      </p>

      {invoiceNo && (
        <div
          className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 border border-[#C5A059] bg-[#FBF8F2] px-4 py-3 text-sm"
          data-testid="calculator-invoice-context"
        >
          <span className="font-semibold">Pricing invoice #{invoiceNo}</span>
          {ctxTotal && (
            <span className="text-muted-foreground">
              · current total {money(Number(ctxTotal))} inc GST
            </span>
          )}
          <span className="text-muted-foreground">
            · to apply a discount, use the <strong>Negotiate price</strong> button on the invoice.
          </span>
        </div>
      )}

      {/* The one question */}
      <div className="mt-6 border border-border" data-testid="calc-main-card">
        <div className="border-b border-border bg-[#0A0A0A] px-4 py-2.5">
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-white">
            What will the customer pay?
          </h2>
        </div>
        <div className="space-y-5 p-5">
          <div className="grid grid-cols-[1fr_96px] gap-3">
            <Field label="Total they'll pay (inc GST)">
              <Input
                {...numInput}
                value={agreed}
                onChange={(e) => setAgreed(e.target.value)}
                placeholder="e.g. 600"
                className={`h-12 text-lg ${inputCls}`}
                data-testid="calc-agreed-input"
                autoFocus
              />
            </Field>
            <Field label="GST %">
              <Input
                {...numInput}
                step="0.1"
                value={gst}
                onChange={(e) => setGst(e.target.value)}
                className={`h-12 ${inputCls}`}
                data-testid="calc-gst-input"
              />
            </Field>
          </div>

          <div className="divide-y divide-border border border-border">
            <Row
              label="Ex-GST price (subtotal)"
              value={money(hasAgreed ? split.exGst : 0)}
              testid="calc-exgst-output"
            />
            <Row
              label={`GST ${gstRate}%`}
              value={money(hasAgreed ? split.gst : 0)}
              testid="calc-gst-output"
            />
            <Row
              label="Total (inc GST)"
              value={money(hasAgreed ? split.incGst : 0)}
              strong
              tone="gold"
              testid="calc-total-output"
            />
          </div>
        </div>
      </div>

      <p className="mt-4 border-l-2 border-[#C5A059] bg-[#FBF8F2] px-4 py-3 text-sm text-muted-foreground">
        This screen only shows the numbers — it doesn't change any invoice. To actually apply a
        negotiated price, open the invoice and use the <strong>Negotiate price</strong> button. It
        adds a tidy &ldquo;Negotiated discount&rdquo; line for you and records it in the Audit Trail.
      </p>
    </div>
  );
}
